"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { X, Gavel, ShieldCheck, Paperclip, AlertCircle } from "lucide-react"
import "./PostQuery.css"
import Navbar from "../../components/Navbar/Navbar"
import { useAuth } from "../../contexts/AuthContext"
import { toast } from "react-toastify"

const COURT_CATEGORIES = [
  { id: "#ClientFromHell", label: "Client From Hell", icon: "👹" },
  { id: "#MedicalMadness", label: "Medical Madness", icon: "🩺" },
  { id: "#DesignDisaster", label: "Design Disaster", icon: "📐" },
  { id: "#EngineeringChaos", label: "Engineering Chaos", icon: "⚙️" },
  { id: "#EditingNightmare", label: "Editing Nightmare", icon: "🎬" },
  { id: "#FriendDispute", label: "Friend Dispute (AITA)", icon: "👥" },
  { id: "#TechCatastrophe", label: "Tech Catastrophe", icon: "💻" }
];

function PostQuery() {
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [selectedCategory, setSelectedCategory] = useState(COURT_CATEGORIES[0].id)
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [authorAlias, setAuthorAlias] = useState("")
  const [attachments, setAttachments] = useState([])
  const [error, setError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [filePreview, setFilePreview] = useState([])
  const navigate = useNavigate()
  const { getIdToken, userData } = useAuth()

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    setAttachments(files)
    const newPreviews = files.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    }))
    setFilePreview(newPreviews)
  }

  const removeFile = (index) => {
    const newAttachments = [...attachments]
    newAttachments.splice(index, 1)
    setAttachments(newAttachments)
    const newPreviews = [...filePreview]
    URL.revokeObjectURL(newPreviews[index].url)
    newPreviews.splice(index, 1)
    setFilePreview(newPreviews)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    if (!title.trim() || !description.trim()) {
      setError("Please provide both a Case Title and the Incident Narrative.")
      return
    }

    setIsSubmitting(true)

    try {
      const attachmentURLs = []
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000"

      // Upload evidence attachments to Cloudinary
      for (const file of attachments) {
        const timestamp = Math.floor(Date.now() / 1000)
        const folder = "court_evidence"
        const public_id = `evidence_${timestamp}_${Math.random().toString(36).substr(2, 7)}`

        try {
          const sigRes = await axios.post(`${apiUrl}/api/generateSignature`, {
            folder,
            public_id,
            timestamp,
          })

          if (sigRes.data && sigRes.data.signature) {
            const formData = new FormData()
            formData.append("file", file)
            formData.append("api_key", process.env.REACT_APP_CLOUDINARY_API_KEY || "359189382674583")
            formData.append("timestamp", timestamp)
            formData.append("folder", folder)
            formData.append("public_id", public_id)
            formData.append("signature", sigRes.data.signature)

            const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "dvqlvcec5"
            const uploadRes = await axios.post(
              `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`,
              formData
            )

            if (uploadRes.data.secure_url) {
              attachmentURLs.push(uploadRes.data.secure_url)
            }
          }
        } catch (uploadErr) {
          console.warn("Evidence upload failed, skipping item:", uploadErr.message)
        }
      }

      // Submit the Case Docket to the backend API
      const token = await getIdToken()
      const res = await axios.post(
        `${apiUrl}/api/court/file-case`,
        {
          title: title.trim(),
          description: description.trim(),
          category: selectedCategory,
          evidence: attachmentURLs,
          isAnonymous,
          authorAlias: isAnonymous ? (authorAlias.trim() || "Anonymous Juror") : (userData?.name || "Juror")
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      toast.success(res.data.message || "Docket filed before The Kangaroo Court! +25 Chips awarded.")
      navigate(`/query/${res.data.caseId}`)
    } catch (err) {
      console.error("Error filing docket:", err)
      setError(err.response?.data?.error || err.message || "Failed to submit case to Court.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      <Navbar />
      <div className="post-query-container">
        <div className="court-docket-header">
          <div className="gavel-badge">
            <Gavel size={28} />
          </div>
          <h1>File a Case Before the Bar</h1>
          <p>
            Submit an unhinged client request, workplace blunder, or personal dispute for peer jury deliberation.
          </p>
        </div>

        {error && (
          <div className="court-error-banner">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="post-query-form">
          {/* Title */}
          <div className="form-group">
            <label htmlFor="title">
              Case Indictment / Docket Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Client demanded a 4K 60fps IMAX video from a 144p WhatsApp screen recording"
              required
            />
          </div>

          {/* Discipline Category */}
          <div className="form-group">
            <label>Discipline / Nature of the Offense</label>
            <div className="court-categories-grid">
              {COURT_CATEGORIES.map((cat) => (
                <button
                  type="button"
                  key={cat.id}
                  className={`court-cat-pill ${selectedCategory === cat.id ? "selected" : ""}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <span className="cat-icon">{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Anonymous File Under Seal */}
          <div className="seal-identity-box">
            <div className="seal-header">
              <label className="seal-checkbox-label">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
                <ShieldCheck size={18} className="shield-icon" />
                <span>File Under Seal (Post Anonymously to the Jury)</span>
              </label>
            </div>
            <p className="seal-desc">
              Your real identity will be redacted from the public docket. Friends will only see your custom alias.
            </p>
            {isAnonymous && (
              <div className="alias-input-row">
                <label htmlFor="alias">Anonymous Alias:</label>
                <input
                  type="text"
                  id="alias"
                  value={authorAlias}
                  onChange={(e) => setAuthorAlias(e.target.value)}
                  placeholder="e.g. The Traumatized Colorist, Anonymous Surgeon, Desperate Engineer"
                />
              </div>
            )}
          </div>

          {/* Incident Narrative */}
          <div className="form-group">
            <label htmlFor="description">
              Statement of Facts & Evidence Narrative <span className="required">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detail the sequence of events. Quote the unhinged emails, WhatsApp messages, or crazy demands verbatim..."
              rows={7}
              required
            />
          </div>

          {/* Evidence Upload */}
          <div className="form-group">
            <label>
              <Paperclip size={16} /> Exhibit Evidence (Screenshots, CAD renders, Blueprints, Audio/Video)
            </label>
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileChange}
              accept="image/*,video/*,application/pdf"
              className="file-input"
            />
            <label htmlFor="file-upload" className="file-upload-btn">
              Choose Evidence Files
            </label>

            {filePreview.length > 0 && (
              <div className="evidence-previews-grid">
                {filePreview.map((file, idx) => (
                  <div key={idx} className="preview-chip">
                    <span>{file.name}</span>
                    <button type="button" onClick={() => removeFile(idx)}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="docket-submit-row">
            <button
              type="submit"
              className="btn-file-docket"
              disabled={isSubmitting}
            >
              <Gavel size={18} />
              {isSubmitting ? "Entering Case into Docket..." : "Docket Case & Earn 25 Chips"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PostQuery
